import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { scaledPx } from "@/styles/scaledFont";

export interface KickerProps extends HTMLAttributes<HTMLSpanElement> {
  /** The dimmer variant — `--d-fg-4` instead of `--d-fg-3`. @default false */
  muted?: boolean;
  children: ReactNode;
}

/** The kicker type itself — shared with `Back` (whose control is a kicker in
 * `--d-fg-2`) and `Field` (whose label is one). 05-SHARED.md §"Общие атомы":
 * `400 10px/14px JetBrains Mono`, letter-spacing .1em, uppercase, `--d-fg-3`.
 *
 * Not `var(--t-label)`: that token is 11px/16px with .02em tracking — a
 * different size, and every prototype kicker uses this one. Written as
 * longhands so a consumer can override one axis without restating the rest. */
export const KICKER_STYLE: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontWeight: 400,
  fontSize: scaledPx(10),
  lineHeight: scaledPx(14),
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: "var(--d-fg-3)",
};

/**
 * The mono label that opens almost every block in v2 — "PARTNER",
 * "YOUR PEOPLE · 3 PARTNERS", a `Field`'s label.
 *
 * Ported from `Kicker` in
 * `docs/design/handoff/design/templates/friends/friends-screen.jsx`.
 */
export function Kicker({
  muted = false,
  className,
  style,
  children,
  ...rest
}: KickerProps): JSX.Element {
  return (
    <span
      className={className}
      style={{
        ...KICKER_STYLE,
        ...(muted ? { color: "var(--d-fg-4)" } : null),
        ...style,
      }}
      {...rest}
    >
      {children}
    </span>
  );
}

export default Kicker;
